import { useEffect, useState } from "react";
import { Image, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { GalleryPhoto, getTournamentPhotosPreview } from "../../services/firebase";
import { Button, Card, EmptyState, Loader, Skeleton, Tag } from "../../ui";
import "./styles.scss";

interface TournamentGalleryCardProps {
  tournamentName: string;
  tournamentId: string;
}

const TournamentGalleryCard = ({ tournamentName, tournamentId }: TournamentGalleryCardProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [previewPhotos, setPreviewPhotos] = useState<GalleryPhoto[]>([]);
  const [totalPhotos, setTotalPhotos] = useState(0);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { previewPhotos, totalCount } = await getTournamentPhotosPreview(tournamentName, 4);
      setPreviewPhotos(previewPhotos);
      setTotalPhotos(totalCount);
    } catch (error) {
      console.error("Error loading gallery preview:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, [tournamentName]);

  return (
    // The outer box keeps .gallery-card, the class the tournament detail page lays its cards out by
    <div className="gallery-card tournament-gallery-card">
      <Card
        className="tournament-gallery-card__panel"
        title={
          <span className="tournament-gallery-card__title">
            GALLERY
            {totalPhotos > 0 && <Tag tone="spark">{totalPhotos}</Tag>}
          </span>
        }
        actions={
          <Button
            variant="tint"
            size="sm"
            icon={<ExternalLink size={14} />}
            onClick={() => navigate(`/dashboard/tournaments/${tournamentId}/gallery`)}
          >
            Manage
          </Button>
        }
      >
        {loading ? (
          <Loader minHeight={120} />
        ) : totalPhotos === 0 ? (
          <EmptyState
            compact
            icon={<Image />}
            title="No photos yet"
            description="Add photos to create a gallery for this tournament"
            action={
              <Button size="sm" onClick={() => navigate(`/dashboard/tournaments/${tournamentId}/gallery`)}>
                Manage gallery
              </Button>
            }
          />
        ) : (
          <div className="gallery-preview-grid">
            {previewPhotos.map((photo, index) => {
              const remaining = totalPhotos - previewPhotos.length;
              const shouldShowMore = remaining > 0 && index === previewPhotos.length - 1;
              return (
                <div key={photo.fullPath} className="preview-tile">
                  <LazyLoadImage
                    src={photo.url}
                    alt={photo.name}
                    effect="blur"
                    wrapperClassName="lazy-image-wrapper"
                    threshold={100}
                    placeholder={<Skeleton height="100%" />}
                    loading="lazy"
                    crossOrigin="anonymous"
                  />
                  {shouldShowMore && (
                    <div className="more-overlay">
                      <p>+{remaining} more</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default TournamentGalleryCard;
